
import React, { useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import { getAppMode } from '../utils/config';
import { DOMAIN_CONFIG } from '../config/domainConfig';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
}

export const SEO: React.FC<SEOProps> = ({ title, description, keywords, image }) => {
  const { tenant } = useTenant();
  const appMode = getAppMode();

  // Determine Defaults based on mode
  const defaultTitleSuffix = tenant?.name ? ` | ${tenant.name}` : (appMode === 'MARKETPLACE' ? ' | Janoobi Studios' : ' | Studio Portal');
  const defaultDescription = "Professional services, bookings, and project management.";
  const defaultKeywords = "studio, booking, services, professional";
  
  // Construct Final Values
  const finalTitle = title ? `${title}${defaultTitleSuffix}` : (tenant?.name || "Studio Portal");
  const finalDesc = description || defaultDescription;
  const finalKeywords = keywords ? keywords.join(', ') : defaultKeywords;
  
  // Use uploaded logo/image or default
  const origin = typeof window !== 'undefined' ? window.location.origin : `https://${DOMAIN_CONFIG.SAAS_ROOT}`;
  const finalImage = image?.startsWith('http') 
      ? image 
      : (image ? `${origin}${image.startsWith('/') ? '' : '/'}${image}` : `${origin}/logo.png`); 

  // Make sure to always default to the provided site logo for general queries if not set
  const logoUrl = tenant?.logoUrl || `${origin}/logo.png`;

  useEffect(() => {
    // 1. Update Title
    document.title = finalTitle;

    // 2. Helper to update meta tags
    const updateMeta = (name: string, content: string, attribute = 'name') => {
        let element = document.querySelector(`meta[${attribute}="${name}"]`);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attribute, name);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    // 3. Helper for Canonical Link (Crucial for Multi-domain SEO)
    const updateCanonical = (url: string) => {
        let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', url);
    };

    // 4. Update Standard Tags
    updateMeta('description', finalDesc);
    updateMeta('keywords', finalKeywords);
    updateMeta('author', tenant?.name || 'Janoobi Studios');

    // 5. Update Open Graph
    updateMeta('og:title', finalTitle, 'property');
    updateMeta('og:description', finalDesc, 'property');
    updateMeta('og:image', finalImage, 'property');
    updateMeta('og:url', window.location.href, 'property');
    updateMeta('og:type', 'website', 'property');
    updateMeta('og:site_name', tenant?.name || 'Janoobi Studios', 'property');

    // 6. Update Twitter
    updateMeta('twitter:card', 'summary_large_image', 'property');
    updateMeta('twitter:title', finalTitle, 'property');
    updateMeta('twitter:description', finalDesc, 'property');
    updateMeta('twitter:image', finalImage, 'property');

    // 7. Update Canonical
    const cleanUrl = window.location.origin + window.location.pathname;
    updateCanonical(cleanUrl);

    // 8. Structured Data for SEO, AEO (Answer Engines) and GEO (Generative Engines)
    // We add a script tag with application/ld+json for LocalBusiness/Organization.
    let schemaScript = document.getElementById('seo-json-ld');
    if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('id', 'seo-json-ld');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
    }

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": tenant?.name || "Janoobi Studios",
        "image": [ finalImage, logoUrl ],
        "logo": logoUrl,
        "description": finalDesc,
        "url": cleanUrl,
        "telephone": tenant?.phone || "",
        "sameAs": []
    };

    schemaScript.textContent = JSON.stringify(schemaData);

  }, [finalTitle, finalDesc, finalImage, finalKeywords, tenant, logoUrl]);

  return null;
};
